import * as React from 'react';

interface Args {
  // minimum delay between onPoll calls if triggered before period, in milliseconds
  pause?: number;
  onPoll: () => void;
  onTimeout?: () => void;
  // how often to poll, in milliseconds
  period: number;
  // when to stop polling, in milliseconds
  timeout?: number;
}

type Rtn = [() => void, () => void];

export const usePoll = ({ pause, onPoll, onTimeout, period, timeout }: Args): Rtn => {
  const [isPolling, setIsPolling] = React.useState<boolean>(false);
  const startPolling = React.useCallback(() => setIsPolling(true), []);
  const stopPolling = React.useCallback(() => setIsPolling(false), []);
  const isPausedRef = React.useRef<boolean>(false);
  const pauseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (isPolling) {
      if (!isPausedRef.current) {
        onPoll();
        if (pause && pause > 0) {
          isPausedRef.current = true;
          pauseTimeoutRef.current = setTimeout(() => {
            isPausedRef.current = false;
          }, pause);
        }
      }

      const intervalId = setInterval(onPoll, period);

      if (timeout === undefined) return () => clearInterval(intervalId);

      const timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        onTimeout?.();
      }, timeout);

      return () => {
        clearTimeout(timeoutId);
        clearInterval(intervalId);
      };
    }
  }, [onPoll, onTimeout, isPolling, timeout]);

  React.useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    };
  }, []);

  return [startPolling, stopPolling];
};
